import { apiClient } from './client';

// Bank Types
export interface Bank {
  id: number;
  name: string;
  iban: string;
  description?: string;
  logo_url?: string;
  website?: string;
  contact_info?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface BankCreate {
  name: string;
  iban: string;
  description?: string;
  logo_url?: string;
  website?: string;
  contact_info?: string;
  is_active?: boolean;
}

export interface BankUpdate {
  name?: string;
  iban?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  contact_info?: string;
  is_active?: boolean;
}

export interface InterestRate {
  id: number;
  bank_id: number;
  bank_name: string;
  rate: number;
  term_days: number;
  min_amount: number;
  max_amount?: number;
  effective_date: string;
  end_date?: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface InterestRateCreate {
  bank_id: number;
  rate: number;
  term_days: number;
  min_amount: number;
  max_amount?: number;
  effective_date: string; // YYYY-MM-DD format
  end_date?: string;
  is_active?: boolean;
}

export interface InterestRateUpdate {
  rate?: number;
  term_days?: number;
  min_amount?: number;
  max_amount?: number;
  effective_date?: string;
  end_date?: string;
  is_active?: boolean;
}

export interface BankListResponse {
  banks: Bank[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface BankSearchParams {
  page?: number;
  page_size?: number;
  is_active?: boolean;
  search?: string;
}

export interface BankStats {
  total_banks: number;
  active_banks: number;
  inactive_banks: number;
  total_rates: number;
  active_rates: number;
  average_rate: number;
  highest_rate: number;
  lowest_rate: number;
}

// Bank API Service
export const bankApi = {
  // List banks with pagination and filtering
  async list(params: BankSearchParams = {}): Promise<BankListResponse> {
    try {
      const response = await apiClient.get('/api/v1/banks', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching banks:', error);
      throw new Error('Bankalar getirilemedi');
    }
  },

  // Get bank by ID
  async getById(id: number): Promise<Bank> {
    try {
      const response = await apiClient.get(`/api/v1/banks/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bank:', error);
      throw new Error('Banka bulunamadı');
    }
  },

  // Create new bank
  async create(bankData: BankCreate): Promise<Bank> {
    try {
      const response = await apiClient.post('/api/v1/banks', bankData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating bank:', error);
      const message = error.response?.data?.detail || 'Banka oluşturulamadı';
      throw new Error(message);
    }
  },

  // Update bank
  async update(id: number, bankData: BankUpdate): Promise<Bank> {
    try {
      const response = await apiClient.put(`/api/v1/banks/${id}`, bankData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating bank:', error);
      const message = error.response?.data?.detail || 'Banka güncellenemedi';
      throw new Error(message);
    }
  },

  // Toggle bank status (activate/deactivate)
  async toggleStatus(id: number): Promise<Bank> {
    try {
      const response = await apiClient.patch(`/api/v1/banks/${id}/toggle-status`);
      return response.data;
    } catch (error: any) {
      console.error('Error toggling bank status:', error);
      const message = error.response?.data?.detail || 'Banka durumu değiştirilemedi';
      throw new Error(message);
    }
  },

  // Delete bank
  async delete(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/banks/${id}`);
    } catch (error: any) {
      console.error('Error deleting bank:', error);
      const message = error.response?.data?.detail || 'Banka silinemedi';
      throw new Error(message);
    }
  },

  // Get bank statistics
  async getStats(): Promise<BankStats> {
    try {
      const response = await apiClient.get('/api/v1/banks/stats/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching bank stats:', error);
      throw new Error('İstatistikler getirilemedi');
    }
  },

  // Interest Rate Management
  rates: {
    // List interest rates for a bank
    async listByBank(bankId: number): Promise<InterestRate[]> {
      try {
        const response = await apiClient.get(`/api/v1/banks/${bankId}/rates`);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching interest rates:', error);
        throw new Error('Faiz oranları getirilemedi');
      }
    },

    // Get all interest rates
    async listAll(): Promise<InterestRate[]> {
      try {
        const response = await apiClient.get('/api/v1/banks/rates');
        return response.data.rates || [];
      } catch (error) {
        console.error('Error fetching all interest rates:', error);
        throw new Error('Faiz oranları getirilemedi');
      }
    },

    // Create interest rate
    async create(rateData: InterestRateCreate): Promise<InterestRate> {
      try {
        const response = await apiClient.post(`/api/v1/banks/${rateData.bank_id}/rates`, rateData);
        return response.data;
      } catch (error: any) {
        console.error('Error creating interest rate:', error);
        const message = error.response?.data?.detail || 'Faiz oranı oluşturulamadı';
        throw new Error(message);
      }
    },

    // Update interest rate
    async update(id: number, rateData: InterestRateUpdate): Promise<InterestRate> {
      try {
        const response = await apiClient.put(`/api/v1/banks/rates/${id}`, rateData);
        return response.data;
      } catch (error: any) {
        console.error('Error updating interest rate:', error);
        const message = error.response?.data?.detail || 'Faiz oranı güncellenemedi';
        throw new Error(message);
      }
    },

    // Delete interest rate
    async delete(id: number): Promise<void> {
      try {
        await apiClient.delete(`/api/v1/banks/rates/${id}`);
      } catch (error: any) {
        console.error('Error deleting interest rate:', error);
        const message = error.response?.data?.detail || 'Faiz oranı silinemedi';
        throw new Error(message);
      }
    }
  }
}; 